import { useCallback, useEffect } from 'react'

import { CursorState } from '@editor/config/constants/cursor'
import { useEditorContext } from '@editor/contexts/Editor'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCursor } from '@editor/store/tool'
import { clamp } from 'lodash'
import { useSelector } from 'react-redux'
import { Vector2Like } from 'three'
import { useThrottledCallback } from 'use-debounce'

import { reorderInspectionGroups, setDraggingItem } from '../../store/inspectionGroup'
import { LayerRect } from './definitions'

let draggingItem: HTMLElement | null = null
function getDraggingItem(key: string): HTMLElement | null {
  if (draggingItem) return draggingItem
  const item = document.querySelector(`[data-element-key="${key}"]`)
  if (item) {
    draggingItem = item as HTMLElement
    return draggingItem
  }
  return null
}

let mouseStartPosition: Vector2Like | null = null
let itemStartPosition: DOMRect | null = null

const DEFAULT_SCROLL_SPEED = 15 // pixels per frame
let autoScrollEnabled = false
let autoScrollDirection: 'up' | 'down' = 'up'
let autoScrollSpeed = DEFAULT_SCROLL_SPEED

/**
 * Continuously scrolls a container upward (overflow-y)
 * until `shouldScrollFlag.value` becomes false.
 *
 * @param container The scrollable div element
 */
function autoScroll(container: HTMLElement) {
  function step() {
    if (!autoScrollEnabled) return

    container.scrollTop =
      autoScrollDirection === 'up'
        ? Math.max(0, container.scrollTop - autoScrollSpeed)
        : container.scrollTop + autoScrollSpeed

    if (container.scrollTop > 0) {
      requestAnimationFrame(step)
    }
  }

  requestAnimationFrame(step)
}

const useDragNDrop = (container: HTMLElement | null) => {
  // Context
  const { updateElementLayerOrders } = useEditorContext()

  // Store
  const dispatch = useAppDispatch()
  const inspectionItems = useSelector((state: RootState) => state.inspectionItems.items)
  const shapes = useSelector((state: RootState) => state.shapes.shapes)
  const draggingItemKey = useSelector((state: RootState) => state.inspectionGroup.draggingItemKey)

  /**
   * Handle grab end event.
   *
   * @param e Mouse event
   */
  const handleGrabEnd = useCallback(() => {
    if (!draggingItemKey || !container) return

    // no matter what, reset the dragging item
    dispatch(setCursor(CursorState.NONE))
    dispatch(setDraggingItem(undefined))
    autoScrollEnabled = false
    autoScrollSpeed = DEFAULT_SCROLL_SPEED

    const unlabeledContainer = document.querySelector('[data-id=unlabeled-elements]')?.parentElement
    const targetItemClone: HTMLElement | null = document.querySelector(`.__clone`)
    const targetItem = getDraggingItem(draggingItemKey)

    if (!targetItem || !unlabeledContainer || !targetItemClone) return

    const targetId = targetItemClone.dataset.id || ''
    const targetIds = targetId.split('||')

    const isGroup = draggingItemKey.startsWith('panel-group-') // a group itself
    const isGrouped = targetItem.classList.contains('grouped') // an element within a group

    draggingItem = null
    mouseStartPosition = null
    itemStartPosition = null

    // need to see if the clone is on the unlabeled container in the case it was dragged out of a group
    if (targetItemClone && unlabeledContainer.contains(targetItemClone) && isGrouped && !isGroup) {
      // just hide it, the filter later will ignore it
      targetItem.style.display = 'none'
    }

    let layerEles: NodeListOf<HTMLElement> = container.querySelectorAll(
      '[data-element-key]:not(.grabbed):has(.grabber),.__clone',
    )
    let layers = Array.from(layerEles).filter((ele) => {
      const isHidden = ele.style.display === 'none'
      const id = ele.getAttribute('data-id') || ''
      const isGrp = ele.getAttribute('data-element-key')?.startsWith('panel-group-')
      const isClone = ele.classList.contains('__clone')
      return (!isHidden && (isGrp || (!targetIds.includes(id) && !isGrp))) || isClone
    })

    // If this is a group and for whatever reason the clone placeholder is missing from the drop container,
    // re-query layers but include the grabbed layer this time so it does not get lost.
    if (isGroup && !layers.includes(targetItemClone)) {
      layerEles = container.querySelectorAll('[data-element-key]:has(.grabber)')
      layers = Array.from(layerEles).filter((ele) => {
        const isHidden = ele.style.display === 'none'
        const id = ele.getAttribute('data-id') || ''
        const isGrp = ele.getAttribute('data-element-key')?.startsWith('panel-group-')
        const isClone = ele.classList.contains('__clone')
        return (!isHidden && (isGrp || (!targetIds.includes(id) && !isGrp))) || isClone
      })
    }

    const newOrder: string[] = []
    const updatedInspectionItemsOrder: { id: string; group_id: string | null; order: number | null }[] = []
    const updatedCylindersOrder: { id: string; group_id: string | null; order: number | null }[] = []
    const updatedPolygonsOrder: { id: string; group_id: string | null; order: number | null }[] = []
    let currentGroupId = ''
    let currentGroupOrder = 0

    // Save the new order of items
    layers.forEach((ele) => {
      const key = ele.getAttribute('data-element-key')?.replace('-clone', '')
      const id = ele.getAttribute('data-id')

      if (!key || !id || ele.classList.contains('removed')) return

      // If we're sorting group, it's a simple re-order.
      if (key?.startsWith('panel-group-')) {
        newOrder.push(key)
        currentGroupId = id
        currentGroupOrder = 0
      } else if (currentGroupId && !isGroup) {
        const ids = id.split('||')

        ids.forEach((itemId) => {
          // Since we don't really know what kind of layer it is, check every possible type.
          const item = inspectionItems.find((it) => it.inspection_item_id === itemId)
          const polygon = shapes.polygons?.find((shape) => shape.shape_id === itemId)
          const cylinder = shapes.cylinders?.find((shape) => shape.shape_id === itemId)

          currentGroupOrder += 1

          if (item) {
            updatedInspectionItemsOrder.push({
              id: itemId,
              group_id: currentGroupId,
              order: currentGroupOrder,
            })
          }

          if (polygon) {
            updatedPolygonsOrder.push({
              id: itemId,
              group_id: currentGroupId,
              order: currentGroupOrder,
            })
          }

          if (cylinder) {
            updatedCylindersOrder.push({
              id: itemId,
              group_id: currentGroupId,
              order: currentGroupOrder,
            })
          }
        })
      }
    })

    // If it's removed, does not mean we should not define anything.
    // We still need to update the order values to reflect its removal.
    if (targetItemClone && unlabeledContainer.contains(targetItemClone)) {
      // we'll just add to all of them. yeah, it might be added to the wrong list,
      // but when updating the orders, it'll be ignored anyway due to mismatched IDs.
      targetIds.forEach((itemId) => {
        updatedInspectionItemsOrder.push({
          id: itemId,
          group_id: null,
          order: null,
        })
        updatedCylindersOrder.push({
          id: itemId,
          group_id: null,
          order: null,
        })
        updatedPolygonsOrder.push({
          id: itemId,
          group_id: null,
          order: null,
        })
      })
    }

    targetItemClone?.remove()

    // Save it
    if (updatedInspectionItemsOrder.length || updatedCylindersOrder.length || updatedPolygonsOrder.length) {
      updateElementLayerOrders(updatedInspectionItemsOrder, updatedCylindersOrder, updatedPolygonsOrder)
    }
    dispatch(reorderInspectionGroups(newOrder))
  }, [inspectionItems, draggingItemKey, container, shapes, updateElementLayerOrders, dispatch])

  /**
   * Handle re-ordering of a layer.
   * This is a simple re-order to swap positions of the dragged item.
   *
   * @param dropContainer The container where the item is being dropped.
   * @param targetItemClone The shadow clone of the dragged item.
   * @param mousePos The current mouse position.
   */
  const handleReOrderLayer = useCallback(
    (dropContainer: HTMLElement, targetItemClone: HTMLElement, mousePos: Vector2Like) => {
      if (!draggingItemKey || !container) return

      const unlabeledElements = document.querySelector('[data-id=unlabeled-elements]')
      const layers = Array.from(
        dropContainer.querySelectorAll('[data-element-key]:not(.shadow-clone):has(.grabber):not(.__clone)'),
      )
      const isGroup = draggingItemKey.startsWith('panel-group-')
      const targetItem = getDraggingItem(draggingItemKey)

      // If there are no layers or only one layer, what's the point? For group that is.
      // For elements, it's possible not to have anything yet, eg: moving an element into the group.
      if (
        !targetItem?.parentElement ||
        !targetItemClone ||
        !layers ||
        layers.length <= (isGroup ? 1 : 0) ||
        !unlabeledElements
      )
        return

      // Keep track of current layer positions
      const oldPositions: LayerRect[] = []
      layers.forEach((ele) => {
        oldPositions.push({
          key: ele.getAttribute('data-element-key') || '',
          rect: ele.getBoundingClientRect(),
        })
      })

      // calculate if mouse position is within the bounds of any of the layers
      const overlaps = oldPositions.map<boolean | 'top' | 'bottom'>(({ rect }) => {
        const midY = rect.top + rect.height / 2
        const isInside =
          ((!isGroup && mousePos.x >= rect.left && mousePos.x <= rect.right) || isGroup) &&
          mousePos.y >= rect.top &&
          mousePos.y <= rect.bottom

        if (!isInside) return false

        return mousePos.y < midY ? 'top' : 'bottom'
      })

      if (!overlaps.filter(Boolean).length) {
        return
      }

      const overlappedLayerIndex = overlaps.findIndex((overlap) => overlap)
      const overlappedLayer = layers[overlappedLayerIndex] as HTMLElement | undefined
      if (!overlappedLayer || !dropContainer.contains(overlappedLayer.parentElement)) return

      targetItemClone.style.display = 'block'

      try {
        if (!isGroup && layers.length === 1) {
          dropContainer.appendChild(targetItemClone)
        } else if (overlaps[overlappedLayerIndex] === 'bottom') {
          overlappedLayer.parentElement?.insertAdjacentElement('afterend', targetItemClone)
        } else {
          overlappedLayer.parentElement?.insertAdjacentElement('beforebegin', targetItemClone)
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error while re-ordering:', err)
      }
    },
    [container, draggingItemKey],
  )

  /**
   * Handle re-ordering of elements into, between or out of groups.
   * When re-ordering elements, before the swap can happen, we must first figure out the group they are being dragged into.
   * Elements must belong to a group so a 're-order' can only happen within a group or between groups.
   * They can also be dragged out of a group.
   *
   * @param targetItemClone The shadow clone of the dragged item.
   * @param mousePos The current mouse position.
   */
  const handleReorderElement = useCallback(
    (targetItemClone: HTMLElement, mousePos: Vector2Like) => {
      if (!draggingItemKey || !container) return

      const elementsPanel = document.getElementById('elements-panel')?.children[0] as HTMLElement | null // it's the immediate child with overflow
      const elementsPanelRect = elementsPanel?.getBoundingClientRect()
      const batchRowRect = document.getElementById('elements-panel-batch-operation')?.getBoundingClientRect()
      const unlabeledElements = document.querySelector('[data-id=unlabeled-elements]')
      const targetItem = getDraggingItem(draggingItemKey)
      if (!targetItem?.parentElement) return

      // If the mouse is over the batch row, we want to scroll the elements list up.
      if (
        batchRowRect &&
        elementsPanel &&
        mousePos.y <= batchRowRect.bottom &&
        mousePos.x >= batchRowRect.left &&
        mousePos.x <= batchRowRect.right
      ) {
        autoScrollSpeed = clamp((batchRowRect.bottom - mousePos.y) / 3, 5, DEFAULT_SCROLL_SPEED)
        if (!autoScrollEnabled) {
          autoScrollEnabled = true
          autoScrollDirection = 'up'
          autoScroll(elementsPanel)
        }
        return
      }

      // If the mouse is beyond the bottom of the elements panel, we want to scroll the elements list down.
      // add a bit of threshold to allow more room for scrolling
      if (
        elementsPanel &&
        elementsPanelRect &&
        mousePos.y >= elementsPanelRect.bottom - 10 &&
        mousePos.x >= elementsPanelRect.left &&
        mousePos.x <= elementsPanelRect.right
      ) {
        autoScrollSpeed = clamp((mousePos.y - elementsPanelRect.bottom + 50) / 3, 5, DEFAULT_SCROLL_SPEED)
        if (!autoScrollEnabled) {
          autoScrollEnabled = true
          autoScrollDirection = 'down'
          autoScroll(elementsPanel)
        }
        return
      }

      // If the mouse is not over the batch row, stop auto-scrolling.
      autoScrollEnabled = false
      autoScrollSpeed = DEFAULT_SCROLL_SPEED

      const groupRects = Array.from(
        container.querySelectorAll('.inspection-group[data-element-key]:not(.shadow-clone):not(.__clone)'),
      )
        .map((ele) => {
          if (!ele.parentElement) return null
          return {
            key: ele.getAttribute('data-element-key') || '',
            rect: ele.parentElement.getBoundingClientRect(),
          }
        })
        .filter((ele): ele is LayerRect => !!ele)

      // Based on mouse position, figure out which group the item is being dragged into.
      const targetGroup = groupRects.find(
        ({ rect }) =>
          mousePos.x >= rect.left && mousePos.x <= rect.right && mousePos.y >= rect.top && mousePos.y <= rect.bottom,
      )

      if (!targetGroup) {
        // If we can't find a group to drop the item into, consider it as being taken out of the group.
        const containerRect = container.getBoundingClientRect()
        if (
          unlabeledElements &&
          (mousePos.x < containerRect.left ||
            mousePos.x > containerRect.right ||
            mousePos.y < containerRect.top ||
            mousePos.y > containerRect.bottom)
        ) {
          targetItemClone.style.display = 'block'
          unlabeledElements.insertAdjacentElement('afterend', targetItemClone)
        }

        return
      }

      // If the item is being dragged into a group, we need to handle that.
      const groupElement: HTMLElement | null =
        container.querySelector(`[data-element-key="${targetGroup.key}"]`)?.parentElement || null
      if (groupElement) {
        handleReOrderLayer(groupElement, targetItemClone, mousePos)
      }
    },
    [container, draggingItemKey, handleReOrderLayer],
  )

  /**
   * Handle reorder of layers.
   * This is throttled to to avoid performance issues.
   *
   * @param mousePos The current mouse position.
   */
  const handleReorder = useThrottledCallback((mousePos: Vector2Like) => {
    if (!draggingItemKey || !container) return

    const targetItemClone: HTMLElement | null = document.querySelector(`[data-element-key="${draggingItemKey}-clone"]`)

    if (!targetItemClone) return

    // Reordering group item itself
    if (draggingItemKey.startsWith('panel-group-')) {
      handleReOrderLayer(container, targetItemClone, mousePos)
      return
    }

    handleReorderElement(targetItemClone, mousePos)
  }, 1000 / 30)

  /**
   * Handle mouse/touch move event.
   *
   * @param e Mouse or touch event
   */
  const handleMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!draggingItemKey || !container) return

      const shadow: HTMLElement | null = document.querySelector('.shadow-clone')
      const targetItem = getDraggingItem(draggingItemKey)
      let targetItemClone: HTMLElement | null = document.querySelector(`[data-element-key="${draggingItemKey}-clone"]`)

      if (!shadow || !targetItem?.parentElement) return

      const { clientX, clientY } = e instanceof TouchEvent ? e.touches[0] : e
      if (!mouseStartPosition) {
        mouseStartPosition = { x: clientX, y: clientY }
      }

      if (!itemStartPosition) {
        itemStartPosition = targetItem.getBoundingClientRect()
      }

      const deltaX = clientX - mouseStartPosition.x
      const deltaY = clientY - mouseStartPosition.y
      const newX = deltaX + itemStartPosition.left
      const newY = deltaY + itemStartPosition.top

      if (!targetItemClone) {
        targetItemClone = document.createElement('div')
        targetItemClone.dataset.elementKey = `${draggingItemKey}-clone`
        targetItemClone.dataset.id = shadow?.dataset.id || ''
        targetItemClone.classList.add('__clone')
        targetItemClone.style.height = '2px'
        targetItemClone.style.width = '100%'
        targetItemClone.style.backgroundColor = 'var(--chakra-colors-primary-500)'
        targetItemClone.style.display = 'none'
        container.appendChild(targetItemClone)
      }

      shadow.setAttribute('style', `position: absolute; left: ${newX}px; top: ${newY}px; z-index: 1000;opacity: 1;`)

      handleReorder({
        x: clientX,
        y: clientY,
      })
    },
    [draggingItemKey, handleReorder, container],
  )

  /**
   * Bound end event listeners to the document to handle grab end.
   * This is necessary to ensure that the grab end event is captured even if the mouse is released outside the element.
   */
  useEffect(() => {
    document.addEventListener('mouseup', handleGrabEnd)
    document.addEventListener('touchend', handleGrabEnd)
    document.addEventListener('mousemove', handleMove)

    return () => {
      document.removeEventListener('mouseup', handleGrabEnd)
      document.removeEventListener('touchend', handleGrabEnd)
      document.removeEventListener('mousemove', handleMove)
    }
  }, [handleGrabEnd, handleMove])
}

export default useDragNDrop
